import type { UserSummary } from '@blackblack/shared';
import { formatCount } from '@blackblack/shared';
import { api, errorMessage } from '../lib/api';
import { patchEntity, useFollowing } from '../lib/entities';
import { Link } from '../lib/router';
import { useAuth } from '../state/auth';
import { useToast } from '../state/toast';
import { Avatar, Button, VerifiedBadge } from './ui/primitives';
import styles from './userRow.module.css';

export function FollowButton({ userId, initial, size = 'sm' }: { userId: string; initial: boolean; size?: 'sm' | 'md' }) {
  const { me, requireAuth } = useAuth();
  const toast = useToast();
  const following = useFollowing(userId, initial);
  if (me?.id === userId) return null;
  const toggle = async () => {
    if (!requireAuth('Log in om creators te volgen')) return;
    patchEntity('user', userId, { following: !following });
    try {
      if (following) await api.delete(`/users/${userId}/follow`);
      else await api.post(`/users/${userId}/follow`);
    } catch (e) {
      patchEntity('user', userId, { following });
      toast(errorMessage(e), { tone: 'error' });
    }
  };
  return (
    <Button size={size} variant={following ? 'secondary' : 'primary'} onClick={toggle}>
      {following ? 'Volgend' : 'Volgen'}
    </Button>
  );
}

export function UserRow({ user, followerCount, following = false, subtitle }: { user: UserSummary; followerCount?: number; following?: boolean; subtitle?: string }) {
  return (
    <div className={styles.row}>
      <Link to={`/u/${user.username}`} className={styles.main}>
        <Avatar user={user} size={48} />
        <span className={styles.text}>
          <span className={styles.name}>
            {user.displayName} {user.verified && <VerifiedBadge />}
          </span>
          <span className={styles.sub}>
            @{user.username}
            {followerCount !== undefined && ` · ${formatCount(followerCount)} volgers`}
            {subtitle && ` · ${subtitle}`}
          </span>
        </span>
      </Link>
      <FollowButton userId={user.id} initial={following} />
    </div>
  );
}

export function CreatorCard({ user, followerCount }: { user: UserSummary; followerCount: number }) {
  return (
    <div className={styles.card}>
      <Link to={`/u/${user.username}`} className={styles.cardLink}>
        <Avatar user={user} size={72} />
        <span className={styles.name}>
          {user.displayName} {user.verified && <VerifiedBadge size={12} />}
        </span>
        <span className={styles.sub}>{formatCount(followerCount)} volgers</span>
      </Link>
      <FollowButton userId={user.id} initial={false} />
    </div>
  );
}
